import {entries, groupBy} from '@benchling/js-utils';
import _ from 'underscore';

import {ImportDisabledObject} from 'config-migration/ConfigMigrationDAO';
import {
  DEFAULT_NAME_MATCHED_OBJECT_TYPES,
  ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP,
} from 'config-migration/import/ObjectSectionConfig';
import {migrationObjectToCategory} from 'config-migration/import/ObjectSectionConfig';
import convertImportIdentifiersToPlans, {
  isInaccessibleIgnoreAction,
  isInaccessibleUpdateAction,
} from 'config-migration/import/planning-logic/convertImportIdentifiersToPlans';
import {
  MigrationObjectPlanById,
  PrimaryObjectPlan,
  BaseMigrationObjectPlan,
  DestinationObject,
  ImportObject,
  SecondaryObjectPlan,
  ChildMigrationObjects,
} from 'config-migration/types/MigrationObjects';
import {PrimaryObjectType, SecondaryObjectType} from 'config-migration/types/ObjectType';

import {MigrationStep} from '../../types/MigrationStep';
import {PlanningTab} from '../../types/MigrationStep';
import {ObjectAction, ObjectActionType} from '../../types/objectActions';
import {PROCEDURE_CHILD_TYPES} from '../import-plan-sections/GrouperPlanSection';
import {isProcedureChildPlannedOnStep, isTemplatePlannedOnStep} from './templateFiltering';

/**
 * Convert all objects into their initial migration plans - including what default action they have and what
 * other possible actions there are.
 *
 * @param importObjects All import objects from the source (that need to be planned)
 * @param destinationObjects All destination objects (that can potentially be selected to be mapped to an
 *  import object)
 * @returns An array of Objects and their default planning state (MigrationObjectPlan)
 */
export function getInitialMigrationPlans(
  importObjects: Array<ImportObject>,
  destinationObjects: Array<DestinationObject>
): Array<PrimaryObjectPlan> {
  const objectTypeToImportObjects = _.groupBy(importObjects, 'objectType');

  // For each migration step, get initial plans for all object types in that migration step.
  return Object.entries(ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP).flatMap(
    ([migrationStep, objectTypesForStep]): Array<PrimaryObjectPlan> =>
      objectTypesForStep.flatMap((objectType) => {
        const importObjectsOfType = objectTypeToImportObjects[objectType] || [];
        const {baseMigrationObjectPlans, destinationObjectsOfType} = getBaseMigrationObjectPlans(
          importObjectsOfType,
          destinationObjects,
          objectType,
          migrationStep as MigrationStep
        );
        // Convert all BaseMigrationObjectPlans into MigrationObjectPlans using information on
        // their related ImportObject and any previous or default selected actions.
        return baseMigrationObjectPlans.map((plan) => {
          const selectedAction: ObjectAction = plan.selectedAction;
          const importObject = importObjects.find((obj) => obj.identifier.provenanceUri === plan.objectId)!;
          return {
            ...plan,
            selectedAction,
            objectType: importObject.objectType as PrimaryObjectType,
            objectAttributes: importObject.objectAttributes,
            childMigrationObjectsByType: getChildMigrationObjectsByType(
              selectedAction,
              importObject.childObjects,
              destinationObjectsOfType,
              importObject.unplannableChildObjects
            ),
          };
        });
      })
  );
}

function getChildMigrationObjectsByType(
  selectedAction: ObjectAction,
  importChildObjects: Array<ImportObject>,
  destinationObjectsOfType: Array<DestinationObject>,
  unplannableChildObjects: Array<ImportDisabledObject>,
  provenanceUriToPreviouslySelectedAction: Map<string, ObjectAction> = new Map()
): Map<SecondaryObjectType, ChildMigrationObjects> {
  const childMigrationPlans = getChildMigrationPlans(
    selectedAction,
    importChildObjects,
    destinationObjectsOfType,
    provenanceUriToPreviouslySelectedAction
  );
  const subtractedChildObjects = getSubtractedChildObjects(
    selectedAction,
    destinationObjectsOfType,
    childMigrationPlans
  );

  const childMigrationPlansByType = _.groupBy(childMigrationPlans, 'objectType');
  const subtractedChildObjectsByType = _.groupBy(subtractedChildObjects, 'objectType');
  const unplannableChildObjectsByType = _.groupBy(unplannableChildObjects, 'type');

  const childMigrationObjectsByType = new Map();
  const allChildTypes = new Set([
    ...Object.keys(childMigrationPlansByType),
    ...Object.keys(subtractedChildObjectsByType),
    ...Object.keys(unplannableChildObjectsByType),
  ]);
  for (const childType of allChildTypes) {
    const childMigrationPlans = childMigrationPlansByType[childType] || [];
    const subtractedChildObjects = subtractedChildObjectsByType[childType] || [];
    const unplannableChildObjects = unplannableChildObjectsByType[childType] || [];
    childMigrationObjectsByType.set(childType, {
      childMigrationPlans,
      subtractedChildObjects,
      unplannableChildObjects,
    });
  }
  return childMigrationObjectsByType;
}

interface BaseMigrationObjectAndDestinationIdentifiers {
  baseMigrationObjectPlans: Array<BaseMigrationObjectPlan>;
  destinationObjectsOfType: Array<DestinationObject>;
}

function isObjectPlannedOnStep(
  obj: {objectAttributes: object; objectType?: string},
  objectType: PrimaryObjectType,
  stepOrTab: MigrationStep | PlanningTab
): boolean {
  if (objectType === PrimaryObjectType.Template) {
    return isTemplatePlannedOnStep(obj as ImportObject, stepOrTab);
  }
  if (PROCEDURE_CHILD_TYPES.has(objectType)) {
    return isProcedureChildPlannedOnStep(obj as ImportObject, stepOrTab);
  }
  return true;
}

/**
 * Converts importObjects and destinationObjects of the current objectType into
 * migration object plans.
 *
 * If these objects have previously been updated and need to be replanned,
 * uses provenanceUriToPreviouslySelectedAction to do the needed updates.
 *
 * @param importObjectsOfType Import objects with the same type as the passed objectType
 * @param destinationObjects All destination objects
 * @param objectType What type of objects to generate plans for
 * @param stepOrTab The current step/tab in the migration (either MigrationStep or PlanningTab)
 * @param provenanceUriToPreviouslySelectedAction A mapping from Provenance URI to the previously selected
 *  action. If not passed, the method creates the initial object plans.
 */
function getBaseMigrationObjectPlans(
  importObjectsOfType: Array<ImportObject>,
  destinationObjects: Array<DestinationObject>,
  objectType: PrimaryObjectType,
  stepOrTab: MigrationStep | PlanningTab,
  provenanceUriToPreviouslySelectedAction: Map<string, ObjectAction> = new Map()
): BaseMigrationObjectAndDestinationIdentifiers {
  const importIdentifiers = importObjectsOfType
    .filter((obj) => isObjectPlannedOnStep(obj, objectType, stepOrTab))
    .map((importObject) => importObject.identifier);
  const destinationObjectsOfType = destinationObjects
    .filter((obj) => obj.objectType === objectType)
    .filter((obj) => isObjectPlannedOnStep(obj, objectType, stepOrTab));
  const destinationIdentifiers = destinationObjectsOfType.map(
    (destinationObject) => destinationObject.identifier
  );

  const baseMigrationObjectPlans = convertImportIdentifiersToPlans(
    importIdentifiers,
    destinationIdentifiers,
    DEFAULT_NAME_MATCHED_OBJECT_TYPES.includes(objectType),
    provenanceUriToPreviouslySelectedAction
  );

  return {baseMigrationObjectPlans, destinationObjectsOfType};
}

export function getChildMigrationPlans(
  parentSelectedAction: ObjectAction,
  childImportObjects: Array<ImportObject>,
  destinationObjects: Array<DestinationObject>,
  childProvenanceURIToPreviouslySelectedAction: Map<string, ObjectAction> = new Map()
): Array<SecondaryObjectPlan> {
  if (parentSelectedAction.type !== ObjectActionType.UPDATE || childImportObjects.length === 0) {
    return [];
  }
  const targetDestinationObject = destinationObjects.find(
    (destinationObject) =>
      destinationObject.identifier.apiIdentifier === parentSelectedAction.target.apiIdentifier
  )!;

  const allChildMigrationPlans: Array<SecondaryObjectPlan> = [];
  const childImportObjectsByType = _.groupBy(childImportObjects, 'objectType');
  for (const objectType of Object.keys(childImportObjectsByType)) {
    // Collect import and destination children of the same type
    const importChildIdentifiersOfType = childImportObjectsByType[objectType].map(
      (importObject) => importObject.identifier
    );
    const destinationChildIdentifiersOfType = targetDestinationObject.childObjects
      .filter((destinationChild) => destinationChild.objectType === objectType)
      .map((destinationChild) => destinationChild.identifier);

    // Convert to child migration plans
    const baseChildMigrationPlans = convertImportIdentifiersToPlans(
      importChildIdentifiersOfType,
      destinationChildIdentifiersOfType,
      true,
      childProvenanceURIToPreviouslySelectedAction
    );
    // Append to results
    baseChildMigrationPlans.forEach((plan) => {
      const childImportObject = childImportObjects.find(
        (obj) => obj.identifier.provenanceUri === plan.objectId
      )!;

      allChildMigrationPlans.push({
        ...plan,
        objectType: childImportObject.objectType as SecondaryObjectType,
        childMigrationObjectsByType: getChildMigrationObjectsByType(
          plan.selectedAction,
          childImportObject.childObjects,
          targetDestinationObject.childObjects,
          childImportObject.unplannableChildObjects,
          childProvenanceURIToPreviouslySelectedAction
        ),
      });
    });
  }
  return allChildMigrationPlans;
}

/**
 * For a selected update action on a source object, get destination child objects that do not exist on the
 * source and are not already archived. This is used to display archived and removed actions in the import
 * wizard.
 */
export function getSubtractedChildObjects(
  parentSelectedAction: ObjectAction,
  destinationObjects: Array<DestinationObject>,
  childMigrationPlans: Array<BaseMigrationObjectPlan>
): Array<DestinationObject> {
  if (parentSelectedAction.type !== ObjectActionType.UPDATE) {
    return [];
  }
  const targetDestinationObject = destinationObjects.find(
    (destinationObject) =>
      destinationObject.identifier.apiIdentifier === parentSelectedAction.target.apiIdentifier
  )!;

  const foundChildProvenanceUris = new Set();
  childMigrationPlans.forEach((childPlan) => {
    if (childPlan.selectedAction.type === ObjectActionType.UPDATE) {
      foundChildProvenanceUris.add(childPlan.selectedAction.target.provenanceUri);
    }
  });
  return targetDestinationObject.childObjects.filter(
    (destinationChild) =>
      destinationChild.identifier.archived === false &&
      !foundChildProvenanceUris.has(destinationChild.identifier.provenanceUri)
  );
}

/**
 * Every time plans are updated by the user, we need to re-generate plans for all objects of the type(s) that
 * were updated. This is because the list of additional actions are affected by the selected actions of
 * objects of the same type that are planned on the same step. Given a set of updated plans, return the
 * full set of plans to update in the current step.
 *
 * @param currentMigrationStep Optional. Legacy UI provides this for step-based filtering.
 *                             Tabs UI omits this - template filtering happens via attributes.
 */
export function getMigrationPlansToUpdate(
  updatedMigrationPlans: Array<PrimaryObjectPlan>,
  currentMigrationPlans: Array<PrimaryObjectPlan>,
  currentMigrationStep?: MigrationStep
): Array<PrimaryObjectPlan> {
  const updatedPlansByType = _.groupBy(updatedMigrationPlans, 'objectType');
  const updatedPlansById = _.indexBy(updatedMigrationPlans, 'objectId');

  return [
    ...currentMigrationPlans.filter((migrationPlan) => {
      if (!updatedPlansByType[migrationPlan.objectType]) {
        return false;
      }
      if (updatedPlansById[migrationPlan.objectId]) {
        return false;
      }

      // Templates, ProcessTaskSchemas, and ProcessFlowchartConfigVersions can each
      // appear in multiple categories/steps (TEMPLATES vs PROCESS_TASK_SCHEMAS).
      // Only include plans from the same category/step as the updated plans to
      // avoid cross-category replanning.
      if (
        migrationPlan.objectType === PrimaryObjectType.Template ||
        migrationPlan.objectType === PrimaryObjectType.ProcessTaskSchema ||
        migrationPlan.objectType === PrimaryObjectType.ProcessFlowchartConfigVersion
      ) {
        if (currentMigrationStep) {
          if (migrationPlan.objectType === PrimaryObjectType.Template) {
            return isTemplatePlannedOnStep(migrationPlan, currentMigrationStep);
          } else if (
            migrationPlan.objectType === PrimaryObjectType.ProcessTaskSchema ||
            migrationPlan.objectType === PrimaryObjectType.ProcessFlowchartConfigVersion
          ) {
            return isProcedureChildPlannedOnStep(migrationPlan, currentMigrationStep);
          }
        } else {
          const updatedPlansOfType = updatedPlansByType[migrationPlan.objectType] || [];
          if (updatedPlansOfType.length > 0) {
            return (
              migrationObjectToCategory(migrationPlan) === migrationObjectToCategory(updatedPlansOfType[0])
            );
          }
        }
      }
      return true;
    }),
    ...updatedMigrationPlans,
  ];
}

/**
 * Using initial importObjects and destinationObjects, convert all objects into their initial migration plans -
 * including what default action they have and what other actions are available to them.
 *
 * This method assumes that getInitialMigrationPlans has already been run.
 *
 * @param importObjects All import objects from the source (that need to be planned)
 * @param destinationObjects All destination objects (that can potentially be selected to be mapped to an
 *  import object)
 * @param updatedMigrationPlans The list of plans that have changed based on the user's most recent selections.
 * @param currentMigrationPlans The list of the current plans based on the initial plans and the user's
 *  previous selections.
 * @param currentMigrationStep Optional. Legacy UI provides this for step-based filtering.
 *                             Tabs UI omits this - the tab is determined from the plan's attributes.
 * @returns An array of Objects and their updated planning state (MigrationObjectPlan)
 */
export function updateMigrationPlans(
  importObjects: Array<ImportObject>,
  destinationObjects: Array<DestinationObject>,
  updatedMigrationPlans: Array<PrimaryObjectPlan>,
  currentMigrationPlans: Array<PrimaryObjectPlan>,
  currentMigrationStep?: MigrationStep
): Array<PrimaryObjectPlan> {
  const plansToUpdate = getMigrationPlansToUpdate(
    updatedMigrationPlans,
    currentMigrationPlans,
    currentMigrationStep
  );
  const migrationProvenanceURIs = plansToUpdate.map((plan) => plan.objectId);
  const objectTypeToMigrationPlans = groupBy(plansToUpdate, (plan: PrimaryObjectPlan) => plan.objectType);

  return entries(objectTypeToMigrationPlans).flatMap(
    ([objectType, migrationPlansOfType]: [
      PrimaryObjectType,
      Array<PrimaryObjectPlan>,
    ]): Array<PrimaryObjectPlan> => {
      const relevantImportObjects = importObjects.filter((obj) =>
        migrationProvenanceURIs.includes(obj.identifier.provenanceUri)
      );

      // Determine the step/tab for filtering. If not provided, derive from the plan's attributes.
      const stepOrTab: MigrationStep | PlanningTab =
        currentMigrationStep ?? migrationObjectToCategory(migrationPlansOfType[0]);

      // TODO: Refactor to take in initialAction so it doesn't need to recomputed
      const provenanceURIToPreviouslySelectedAction = getProvenanceURIToSelectedAction(migrationPlansOfType);
      const {baseMigrationObjectPlans} = getBaseMigrationObjectPlans(
        relevantImportObjects,
        destinationObjects,
        objectType,
        stepOrTab,
        provenanceURIToPreviouslySelectedAction
      );

      const identifierToUpdatedMigrationObjectPlan = _.indexBy(baseMigrationObjectPlans, 'objectId');

      // For each of the pre-existing MigrationObjectPlans, using the related ImportObject and any previously
      // selected actions for it and its Child Plans, return an updated MigrationObjectPlan.
      return migrationPlansOfType.map((migrationPlan) => {
        const importObject = relevantImportObjects.find(
          (obj) => obj.identifier.provenanceUri === migrationPlan.objectId
        )!;
        return {
          ...identifierToUpdatedMigrationObjectPlan[migrationPlan.objectId],
          childMigrationObjectsByType: getChildMigrationObjectsByType(
            migrationPlan.selectedAction,
            importObject.childObjects,
            destinationObjects,
            importObject.unplannableChildObjects,
            provenanceURIToPreviouslySelectedAction
          ),
          objectType: migrationPlan.objectType,
          objectAttributes: migrationPlan.objectAttributes,
        };
      });
    }
  );
}

export function allChildMigrationPlans(
  plan: PrimaryObjectPlan | SecondaryObjectPlan
): Array<SecondaryObjectPlan> {
  return Array.from(plan.childMigrationObjectsByType.values()).flatMap(
    (childPlansAndSubtracted) => childPlansAndSubtracted.childMigrationPlans
  );
}

/**
 * Get mapping from provenance URI to selected actions of all migration plans and their child plans, inclusive
 * of all nested child levels.
 */
function getProvenanceURIToSelectedAction(
  migrationPlans: Array<PrimaryObjectPlan | SecondaryObjectPlan>
): Map<string, ObjectAction> {
  return migrationPlans.reduce((map, migrationPlan) => {
    map.set(migrationPlan.objectId, migrationPlan.selectedAction);
    for (const [objectId, selectedAction] of getProvenanceURIToSelectedAction(
      allChildMigrationPlans(migrationPlan)
    )) {
      map.set(objectId, selectedAction);
    }
    return map;
  }, new Map<string, ObjectAction>());
}

/**
 * Get objects on the destination that are not matched for update by config migration.
 */
export function getUnmatchedDestinationObjects(
  migrationPlansById: MigrationObjectPlanById,
  destinationObjects: Array<DestinationObject>
): Array<DestinationObject> {
  const selectedApiIdsToUpdate = _.compact(
    Object.values(migrationPlansById).map((plan) =>
      plan.selectedAction.type === ObjectActionType.UPDATE ? plan.selectedAction.target.apiIdentifier : null
    )
  );
  return destinationObjects.filter(
    (destinationObject) => !selectedApiIdsToUpdate.includes(destinationObject.identifier.apiIdentifier)
  );
}

export interface FilteredActionsResult {
  showIgnore: boolean;
  filteredAdditionalActions: Array<ObjectAction>;
  showLockIcon: boolean;
}

/**
 * Computes filtered actions for dropdown based on accessibility states and action types.
 * This logic determines which actions should be shown in the UI based on whether
 * objects are inaccessible and what types of actions are available.
 */
export function computeFilteredActions(plan: PrimaryObjectPlan): FilteredActionsResult {
  // Compute filtered actions for dropdown
  const accessibleActions = plan.additionalActions.filter((a) => !isInaccessibleUpdateAction(a));
  const inaccessibleIgnores = plan.additionalActions.filter((a) => isInaccessibleUpdateAction(a));
  const allAreInaccessible = plan.additionalActions.length > 0 && accessibleActions.length === 0;
  const filteredAdditionalActions = allAreInaccessible ? inaccessibleIgnores : plan.additionalActions;

  // Check if any action is an ignore action with "no access" reason
  const hasNoAccessIgnore =
    (plan.initialAction.type === ObjectActionType.IGNORE && plan.initialAction.reason === 'no access') ||
    (plan.selectedAction.type === ObjectActionType.IGNORE && plan.selectedAction.reason === 'no access');
  const excludeIgnore = hasNoAccessIgnore;
  const showIgnore = !excludeIgnore && (!allAreInaccessible || filteredAdditionalActions.length === 0);
  // Count actual available actions
  const availableActions: Array<ObjectAction> = [
    plan.hasProvenanceUriMatch ? plan.initialAction : ({type: ObjectActionType.CREATE} as ObjectAction),
    ...(showIgnore ? [{type: ObjectActionType.IGNORE} as ObjectAction] : []),
    ...(!plan.hasProvenanceUriMatch ? filteredAdditionalActions : []),
  ];
  const showLockIcon = availableActions.length <= 1;

  return {
    showIgnore,
    filteredAdditionalActions,
    showLockIcon,
  };
}

export function isInaccessibleOnlyIgnoreAvailable(plan: BaseMigrationObjectPlan): boolean {
  const allActions = [plan.selectedAction, ...plan.additionalActions];
  return allActions.every((a) => isInaccessibleUpdateAction(a) || isInaccessibleIgnoreAction(a));
}
